import { LDateTime } from '@app/components/date/LDateTime';
import { ListViewChildProps } from '@app/core/list-view/ListView';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { createActionsColumn } from '@app/modules/audit/components/create-actions-column';
import { ShowCustomDataAction } from '@app/modules/audit/components/ShowCustomDataAction';
import { AuditAudio, Seconds } from '@mi-front/core';
import { Chip, Duration, MrtTable } from '@mi-front/ui';
import { Box } from '@mui/material';
import { useIntl } from 'react-intl';
import { RowActions } from '../../../../../../components/table/RowActions';
import { AuditMediaPlayerAction } from '../../components/AuditMediaPlayerAction';
import { DownloadAudioBtn } from '../../components/download-audio/DownloadAudioBtn';
import { ShowTranscriptionAction } from '../../components/ShowTranscriptionAction';

/**
 * Audios table for ListView. Column filters stay off; list filters live in the toolbar.
 */
export function AudiosListResult(props: ListViewChildProps<AuditAudio>) {
  const { $t } = useIntl();
  const station = useActiveStation();
  const {
    data,
    total,
    pagination,
    onPaginationChange,
    sorting,
    onSortingChange,
    isLoading = false,
    isFetching = false,
    showProgress = true,
  } = props;

  return (
    <Box>
      <MrtTable<AuditAudio>
        data={data}
        rowCount={total}
        state={{
          pagination,
          sorting,
          isLoading,
          showProgressBars: showProgress && (isLoading || isFetching),
        }}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        manualPagination
        manualSorting
        enableColumnFilters={false}
        enableToolbarInternalActions={false}
        paginationDisplayMode="default"
        muiTopToolbarProps={{
          sx: {
            height: '4px !important',
            minHeight: '4px !important',
          },
        }}
        columns={[
          {
            header: $t({
              id: 'audit.audiosScreen.props.date',
              defaultMessage: 'Fecha de carga',
            }),
            accessorKey: 'createdAt',
            Cell: ({ row }) => <LDateTime value={row.original.createdAt} showRelative />,
            size: 200,
          },
          {
            header: $t({
              id: 'audit.audiosScreen.props.audioId',
              defaultMessage: 'ID material',
            }),
            accessorKey: 'customId',
            Cell: ({ row }) => (
              <span>
                {row.original.customId}{' '}
                {row.original.stationsGroupId && (
                  <Chip size="sm" type="light">
                    {$t({
                      id: 'audit.audiosScreen.group',
                      defaultMessage: 'Group',
                    })}
                  </Chip>
                )}
              </span>
            ),
            size: 250,
          },
          {
            header: $t({
              id: 'audit.audiosScreen.props.filename',
              defaultMessage: 'Archivo',
            }),
            accessorKey: 'filename',
            size: 200,
          },
          {
            header: $t({
              id: 'audit.audiosScreen.props.duration',
              defaultMessage: 'Duración',
            }),
            accessorKey: 'duration',
            Cell: ({ row }) => <Duration seconds={(row.original.duration / 1000) as Seconds} />,
            size: 100,
          },
          createActionsColumn<AuditAudio>($t, {
            size: 180,
            Cell: ({ row }) => (
              <RowActions>
                <DownloadAudioBtn
                  size="small"
                  stationId={station.id}
                  audioId={row.original.customId}
                />
                <AuditMediaPlayerAction
                  audio={{
                    ...row.original,
                    stationId: station.id,
                  }}
                />
                {row.original.transcription && (
                  <ShowTranscriptionAction
                    customId={row.original.customId}
                    transcription={row.original.transcription}
                  />
                )}
                <ShowCustomDataAction
                  customId={row.original.customId}
                  customData={row.original.customData}
                />
              </RowActions>
            ),
          }),
        ]}
      />
    </Box>
  );
}
